<template>
  <!-- Первый пейвол: бесплатные распознавания или сообщения Зое кончились
       (lib/freeLimits.js, решение владельца 26.10.2026). Вариант теста
       приходит с сервером в user.free_quota: порог, текст (copy a — факт
       лимита, b — от лица Зои) и дизайн (sheet — шторка, zoya — реплика с
       аватаром и списком). События shown / cta / close / register уходят в
       zoya_paywall_events, итоги — вкладка «Пейвол» админки. -->
  <Teleport to="body">
    <Transition name="fp">
      <div v-if="authStore.firstPaywall" class="fixed inset-0 z-[125] flex items-end justify-center" @click.self="close">
        <div class="absolute inset-0 bg-black/40" @click="close" />

        <!-- Дизайн «шторка» -->
        <div v-if="design === 'sheet'" class="relative w-full max-w-[430px] bg-cream rounded-t-3xl px-6 pt-6 pb-sheet-8 z-10">
          <button @click="close" class="absolute top-4 right-4 p-1" aria-label="Закрыть">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#41291A" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <div class="flex justify-center mb-4">
            <img src="/Zoya_avatar_2_small.jpg" class="w-16 h-16 rounded-full object-cover" alt="Зоя" />
          </div>
          <h2 class="text-lg font-bold text-center text-ink mb-2">{{ text.title }}</h2>
          <p class="text-sm text-center text-ink/80 mb-6 leading-relaxed">{{ text.body }}</p>
          <p v-if="authStore.isGuest && !iosSalesDisabled" class="text-12 text-center text-ink/60 mb-4 leading-snug">Регистрация займёт минуту, всё добавленное сохранится.</p>
          <button v-if="!iosSalesDisabled" @click="cta" class="w-full py-3 rounded-xl text-sm font-semibold text-white bg-ink active:bg-[#2a1a10] transition-colors">{{ text.cta }}</button>
          <button v-else @click="close" class="w-full py-3 rounded-xl text-sm font-semibold text-white bg-ink active:bg-[#2a1a10] transition-colors">Хорошо</button>
          <button @click="close" class="w-full py-2.5 mt-1 text-sm text-ink/60">Не сейчас</button>
        </div>

        <!-- Дизайн «реплика Зои» -->
        <div v-else class="relative w-full max-w-[430px] bg-cream rounded-t-3xl px-5 pt-5 pb-sheet-8 z-10">
          <button @click="close" class="absolute top-4 right-4 p-1" aria-label="Закрыть">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#41291A" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <div class="flex items-start gap-3 mt-4 mb-4 pr-6">
            <img src="/Zoya_avatar_2_small.jpg" class="w-11 h-11 rounded-full object-cover shrink-0" alt="Зоя" />
            <div class="fp-bubble">
              <p class="text-15 font-bold text-ink mb-1 leading-snug">{{ text.title }}</p>
              <p class="text-14 text-ink leading-snug">{{ text.body }}</p>
            </div>
          </div>
          <ul class="fp-list mb-6">
            <li v-for="b in benefits" :key="b"><span class="fp-check"><svg width="11" height="9" viewBox="0 0 12 10" fill="none"><path d="M1 5.2 4.3 8.5 11 1.5" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg></span>{{ b }}</li>
          </ul>
          <button v-if="!iosSalesDisabled" @click="cta" class="w-full py-3 rounded-xl text-sm font-semibold text-white bg-ink active:bg-[#2a1a10] transition-colors">{{ text.cta }}</button>
          <button v-else @click="close" class="w-full py-3 rounded-xl text-sm font-semibold text-white bg-ink active:bg-[#2a1a10] transition-colors">Хорошо</button>
          <button @click="close" class="w-full py-2.5 mt-1 text-sm text-ink/60">Не сейчас</button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../store/auth.js'
import api from '../services/api.js'
import { track } from '../analytics.js'
import { iosSalesDisabled } from '../services/sales.js'

const authStore = useAuthStore()
const router = useRouter()

const quota = computed(() => authStore.user?.free_quota || {})
const assigned = computed(() => authStore.firstPaywall?.assignment)
const design = computed(() => (assigned.value?.design || quota.value.design) === 'zoya' ? 'zoya' : 'sheet')
const copy = computed(() => (assigned.value?.copy || quota.value.copy) === 'b' ? 'b' : 'a')
const trigger = computed(() => authStore.firstPaywall?.trigger || 'photo')
const ty = computed(() => authStore.user?.address_form !== 'вы')

const benefits = computed(() => [
  'Фото и голос без ограничений',
  'Чат с Зоей и разбор дня',
  'Недельный отчет и личный план',
])

// Тексты: по причине (распознавания / сообщения / разбор), варианту и обращению
const text = computed(() => {
  const q = quota.value
  const rec = authStore.firstPaywall?.quota?.limit || q.rec_limit || 4
  const t = trigger.value
  const isRec = t === 'photo' || t === 'voice'
  if (copy.value === 'b') {
    if (isRec) return {
      title: 'Бесплатные распознавания закончились',
      body: ty.value
        ? `Мы использовали весь бесплатный лимит: ${rec} ${plural(rec, 'распознавание', 'распознавания', 'распознаваний')} по фото и голосу. Дальше я могу вести твой дневник каждый день: фото, голос, разбор дня и план. Это Премиум.`
        : `Мы использовали весь бесплатный лимит: ${rec} ${plural(rec, 'распознавание', 'распознавания', 'распознаваний')} по фото и голосу. Дальше я могу вести ваш дневник каждый день: фото, голос, разбор дня и план. Это Премиум.`,
      cta: 'Продолжить с Зоей',
    }
    if (t === 'chat') return {
      title: 'Продолжим?',
      body: ty.value
        ? 'С Премиумом я отвечаю без ограничений, вижу твой дневник и веду план.'
        : 'С Премиумом я отвечаю без ограничений, вижу ваш дневник и веду план.',
      cta: 'Продолжить с Зоей',
    }
    return {
      title: 'Продолжим?',
      body: ty.value ? 'Завтра разберу снова. С Премиумом разбираю в любой момент, сколько захочешь.' : 'Завтра разберу снова. С Премиумом разбираю в любой момент, сколько захотите.',
      cta: 'Продолжить с Зоей',
    }
  }
  if (isRec) return {
    title: 'Бесплатные распознавания закончились',
    body: `В пробном доступе ${rec} ${plural(rec, 'распознавание', 'распознавания', 'распознаваний')} по фото и голосу, и они использованы. С Премиумом распознавание не ограничено, плюс чат с Зоей, разбор дня и недельный отчет.`,
    cta: 'Подключить Премиум',
  }
  if (t === 'chat') return {
    title: 'Продолжим?',
    body: 'В пробном доступе чат с Зоей ограничен. С Премиумом Зоя отвечает без ограничений, видит дневник и ведёт план.',
    cta: 'Подключить Премиум',
  }
  return {
    title: 'Продолжим?',
    body: 'Следующий разбор завтра после 5:00. С Премиумом разбор доступен в любой момент.',
    cta: 'Подключить Премиум',
  }
})

function plural(n, one, few, many) {
  const m10 = n % 10, m100 = n % 100
  if (m10 === 1 && m100 !== 11) return one
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few
  return many
}

function report(event) {
  api.post('/paywall/event', { event, trigger: trigger.value }).catch(() => {})
  const name = {shown:'paywall_shown',cta:'paywall_cta',register:'paywall_cta',close:'paywall_closed'}[event]
  if(name) track(name, {surface:'first',trigger:trigger.value,assignment_id:assigned.value?.id,exposure_id:authStore.firstPaywall?.exposureId})
}

watch(() => authStore.firstPaywall, (v) => {
  if (v) {
    report('shown')
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})
onUnmounted(() => { document.body.style.overflow = '' })

function close() {
  report('close')
  authStore.firstPaywall = null
}

// Гость на странице тарифов попадёт в регистрацию сам (SubscriptionPage.needsAccount)
function cta() {
  report(authStore.isGuest ? 'register' : 'cta')
  authStore.firstPaywall = null
  // Аргументы человек только что прочитал в пейволе — открываем сразу на тарифах
  router.push('/subscription?from=paywall')
}
</script>

<style scoped>
.fp-bubble {
  position: relative;
  flex: 1;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 10px 12px;
  font-family: Inter, sans-serif;
}
.fp-bubble::before {
  content: '';
  position: absolute;
  left: -7px;
  top: 16px;
  width: 12px;
  height: 12px;
  background: #fff;
  border-left: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  transform: rotate(45deg);
}
.fp-list { list-style: none; margin: 0; padding: 0 2px; font-family: Inter, sans-serif; }
.fp-list li { display: flex; align-items: center; gap: 10px; padding: 7px 0; font-size: var(--fs-14); color: var(--ink); border-top: 1px solid var(--line); }
.fp-list li:first-child { border-top: 0; }
.fp-check {
  width: 20px; height: 20px; flex: none; border-radius: 50%;
  background: #D6AC67; color: #fff; display: flex; align-items: center; justify-content: center;
}
.fp-enter-active, .fp-leave-active { transition: opacity .2s ease; }
.fp-enter-from, .fp-leave-to { opacity: 0; }
</style>
